import { styled, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { sv } from 'date-fns/locale';
import { IntervalEntryConfig } from 'api/report/types';
import { MetadataDisplay } from 'components/MetadataDisplay';
import Symbol from 'components/Symbol';
import Tooltip from 'components/interface/tooltip/Tooltip';
import {
    getIntervalDraftDistance,
    getIntervalDraftMinutes,
} from 'report/logic/interval/calculations';
import {
    IntervalEntryDraft,
    IntervalEntryValidation,
} from 'report/logic/interval/editing';
import {
    formatIntervalKind,
    formatIntervalRange,
    formatMinutes,
} from './formatting';

const EntryRow = styled('button', {
    shouldForwardProp: prop =>
        prop !== '$selected' && prop !== '$mobile' && prop !== '$invalid',
})<{ $selected: boolean; $mobile: boolean; $invalid: boolean }>(
    ({ theme, $selected, $mobile, $invalid }) => ({
        'display': 'grid',
        'gridTemplateColumns': 'minmax(0, 1fr) auto 6em',
        'alignItems': 'start',
        'alignContent': 'center',
        'gap': theme.spacing(2),
        'width': '100%',
        'minHeight': 48,
        'padding': theme.spacing(1, 1.5),
        'border': 0,
        'borderRadius': 0,
        'backgroundColor': $invalid
            ? theme.palette.error[50]
            : $selected
              ? theme.palette.primary[50]
              : theme.palette.background.paper,
        'boxShadow': $invalid
            ? `inset 3px 0 0 ${theme.palette.error.main}`
            : undefined,
        'color': theme.palette.text.primary,
        'fontFamily': 'inherit',
        'fontSize': theme.typography.body2.fontSize,
        'textAlign': 'left',
        'cursor': 'pointer',
        ...($mobile && {
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: theme.spacing(0.25, 0.75),
        }),
        '&:hover': {
            backgroundColor: $invalid
                ? theme.palette.error[100]
                : $selected
                  ? theme.palette.primary[50]
                  : $mobile
                    ? theme.palette.primary[25]
                    : theme.palette.grey[50],
        },
    })
);

const EntryHeading = styled('div')(({ theme }) => ({
    display: 'flex',
    minWidth: 0,
    alignItems: 'baseline',
    gap: theme.spacing(1),
    overflow: 'hidden',
    whiteSpace: 'nowrap',
}));

const ErrorIndicator = styled('span')({
    display: 'inline-flex',
    alignSelf: 'center',
    flexShrink: 0,
});

const TimeRange = styled('div', {
    shouldForwardProp: prop => prop !== '$mobile',
})<{ $mobile: boolean }>(({ theme, $mobile }) => ({
    display: 'grid',
    // Reserve space for next-day dates so times align across all rows.
    gridTemplateColumns: '8ch auto 8ch',
    alignItems: 'baseline',
    columnGap: theme.spacing(0.75),
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    ...($mobile && {
        gridTemplateColumns: 'max-content auto max-content',
        columnGap: theme.spacing(0.5),
    }),
}));

const Duration = styled('div')({
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
});

const TooltipRange = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    ...theme.typography.caption,
    fontWeight: 700,
    whiteSpace: 'nowrap',
}));

const RangeDivider = styled('span')({
    height: 2,
    width: 8,
    flexShrink: 0,
    backgroundColor: 'currentColor',
});

interface Props {
    draft: IntervalEntryDraft;
    validation: IntervalEntryValidation;
    entryKinds: IntervalEntryConfig[];
    timeZone: string;
    selected: boolean;
    onClick: () => void;
    isMobile?: boolean;
}

export function DayCardEntry({
    draft,
    validation,
    entryKinds,
    timeZone,
    selected,
    onClick,
    isMobile = false,
}: Props) {
    const { errors } = validation;
    const showError = !selected && Object.keys(errors).length > 0;
    const minutes = getIntervalDraftMinutes(draft, errors);
    const distanceKm = getIntervalDraftDistance(draft, errors);
    const range =
        minutes !== null ? formatIntervalRange(draft, timeZone) : null;
    const dateHelpers =
        minutes !== null
            ? [draft.startDate, draft.endDate].map(date => {
                  return date !== draft.reportingDate
                      ? format(parseISO(date), 'EEE d/M', { locale: sv })
                      : '';
              })
            : [];
    const rangeTooltip = range ? (
        <TooltipRange>
            <span>
                {range.startDate} {range.startTime}
            </span>
            <RangeDivider />
            <span>
                {range.endDate && `${range.endDate} `}
                {range.endTime}
            </span>
        </TooltipRange>
    ) : null;

    const distanceLabel =
        distanceKm !== null ? `${distanceKm.toLocaleString('sv-SE')} km` : '';
    const metadataParts = isMobile
        ? [draft.location?.name ?? '', distanceLabel]
        : [
              draft.location
                  ? `${draft.location.name}${draft.location.group ? ` (${draft.location.group.name})` : ''}`
                  : '',
              draft.reason?.name ?? '',
              draft.isSick ? 'Sjukfrånvaro' : '',
              distanceLabel,
          ];

    return (
        <EntryRow
            type='button'
            aria-invalid={showError || undefined}
            $invalid={showError}
            $selected={selected}
            $mobile={isMobile}
            onClick={onClick}>
            <EntryHeading>
                {showError && (
                    <Tooltip title='Raden innehåller fel. Öppna raden för att rätta uppgifterna.'>
                        <ErrorIndicator
                            role='img'
                            aria-label='Raden innehåller fel'>
                            <Symbol symbol='error' color='error' filled />
                        </ErrorIndicator>
                    </Tooltip>
                )}
                <Typography
                    variant='body2'
                    fontWeight={500}
                    noWrap
                    sx={{ minWidth: 0 }}>
                    {formatIntervalKind(draft.kind, entryKinds)}
                </Typography>
                <MetadataDisplay
                    parts={metadataParts}
                    noWrap
                    color='text.secondary'
                    sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'max-content' }}
                />
                {!isMobile && draft.note.trim().length > 0 && (
                    <Symbol
                        symbol='comment'
                        size='sm'
                        color='textSecondary'
                        sx={{ alignSelf: 'center', flexShrink: 0 }}
                        tooltip={
                            <Typography
                                variant='caption'
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                }}>
                                {draft.note}
                            </Typography>
                        }
                    />
                )}
            </EntryHeading>
            <Tooltip
                title={rangeTooltip}
                componentsProps={{ tooltip: { sx: { maxWidth: 'none' } } }}>
                <TimeRange $mobile={isMobile}>
                    <Typography variant='body2' align='right'>
                        {draft.startTime || '—'}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        –
                    </Typography>
                    <Typography variant='body2'>
                        {draft.endTime || '—'}
                    </Typography>
                    {dateHelpers.some(Boolean) && (
                        <>
                            <Typography
                                variant='caption'
                                color='text.secondary'
                                align='right'>
                                {dateHelpers[0] || '\u00a0'}
                            </Typography>
                            <span />
                            <Typography
                                variant='caption'
                                color='text.secondary'>
                                {dateHelpers[1] || '\u00a0'}
                            </Typography>
                        </>
                    )}
                </TimeRange>
            </Tooltip>
            {!isMobile && (
                <Duration>
                    <Typography variant='body2' fontWeight={600}>
                        {minutes !== null ? formatMinutes(minutes) : '—'}
                    </Typography>
                    {!errors.breakMinutes && Number(draft.breakMinutes) > 0 && (
                        <Typography variant='caption' color='text.secondary'>
                            Rast {draft.breakMinutes} min
                        </Typography>
                    )}
                </Duration>
            )}
        </EntryRow>
    );
}
